/*
 * ScrollToTop — react-router-dom v6 helper that snaps the window to
 * the top whenever the path changes.
 *
 * Why it exists: by default, React Router preserves scroll position
 * across route changes (the browser still owns scroll state). So a
 * user reading the bottom of /portfolios who clicks "Lifeline" lands
 * on /lifeline already-scrolled. Users perceive this as a bug ("the
 * page opened in the middle"). This component fixes that globally
 * by listening to pathname changes and resetting scroll to (0, 0).
 *
 * - Listens only on pathname change (not search) so quietly updating
 *   query params (e.g. tab=calendar → tab=dates on the SAME page)
 *   doesn't jump the user away from where they were reading.
 * - Uses `behavior: 'auto'` (instant) instead of smooth — smooth
 *   scrolling 4000px on a navigation feels janky.
 * - No DOM render; just an effect. Mount once at the router root.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    // `window.scrollTo` is the canonical reset. Also reset
    // documentElement/scrollingElement for browsers where the
    // <html> element owns scroll (rare but happens with custom
    // overflow CSS — defence-in-depth).
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
    } catch (_) { /* noop on environments without window (SSR) */ }
  }, [pathname]);
  return null;
};

export default ScrollToTop;
